import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import notification from "antd/es/notification";
import { defineMessages, useIntl } from "react-intl";

import { EntityTypesSupportingRoutes } from "@mapmycustomers/shared/types/entity";
import Route from "@mapmycustomers/shared/types/entity/Route";
import { nameComparator } from "@mapmycustomers/shared/util/comparator";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";
import defaultFilterOption from "@mapmycustomers/shared/util/input/defaultFilterOption";
import { SelectField } from "@mapmycustomers/ui";

import CreateRoutePureModal from "@app/component/AddToRouteModal/CreateRoutePureModal";
import { CreateRouteFormPayload } from "@app/component/AddToRouteModal/store/actions";
import ButtonLink from "@app/component/ButtonLink";
import { filterRoutes } from "@app/component/leadFinder/store/actions";
import {
  areRoutesLoading,
  getAddRecordsAvailableRoutes,
} from "@app/component/leadFinder/store/selectors";
import RecordRouteModel from "@app/component/leadFinder/types/RecordRouteModel";
import { isGeocodeMmcLimitReached, isGeocodeOrgLimitReached } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import showGeocodeLimitAlertModal from "@app/util/geocodeLimitAlert/showGeocodeLimitAlertModal";

import styles from "./Routes.module.scss";

const NEW_ROUTE_PREFIX = "new__";

const isRouteId = (value: RecordRouteModel): value is Route["id"] => typeof value === "number";

const mapRouteRecordToValue = (value: RecordRouteModel): string =>
  isRouteId(value) ? value.toString() : `${NEW_ROUTE_PREFIX}${value.name}`;

const messages = defineMessages({
  newRouteButton: {
    id: "leadFinder.addRecordsWizard.routes.newRouteButtonTitle",
    defaultMessage: "+ Create New Route",
    description: "Create new route button for Add Records",
  },
  newRouteName: {
    id: "leadFinder.addRecordsWizard.routes.newRouteName",
    defaultMessage: "{name} [NEW]",
    description: "New route name for Add Records",
  },
  placeholder: {
    id: "leadFinder.addRecordsWizard.routes.placeholder",
    defaultMessage: "Click or type to select routes",
    description: "Select Route placeholder for Add Records",
  },
  routeAddedDescription: {
    id: "leadFinder.addRecordsWizard.routes.routeAdded.description",
    defaultMessage:
      "Please note, this route will not be saved until Leads are added as Companies or People.",
    description: "Route created description for Add Records",
  },
  routeAddedMessage: {
    id: "leadFinder.addRecordsWizard.routes.routeAdded.message",
    defaultMessage: "New Route Created",
    description: "Route created message for Add Records",
  },
});

interface RoutesProps {
  entityType: EntityTypesSupportingRoutes;
  onChange?: (value: RecordRouteModel[]) => void;
  value?: RecordRouteModel[];
}

const Routes = ({
  entityType,
  isMmcGeocodeLimitReached,
  isOrgGeocodeLimitReached,
  loading,
  onChange,
  onSearch,
  routes,
  value,
}: RoutesProps & typeof mapDispatchToProps & ReturnType<typeof mapStateToProps>) => {
  const intl = useIntl();
  const [createRouteModalVisible, showCreateRouteModal, hideCreateRouteModal] = useBoolean();

  const handleCreateRouteClick = useCallback(() => {
    if (isMmcGeocodeLimitReached || isOrgGeocodeLimitReached) {
      showGeocodeLimitAlertModal(intl, isMmcGeocodeLimitReached);
    } else {
      showCreateRouteModal();
    }
  }, [intl, isMmcGeocodeLimitReached, isOrgGeocodeLimitReached, showCreateRouteModal]);

  const parsedValue = useMemo(() => (value ?? []).map(mapRouteRecordToValue), [value]);

  const existingRoutes: (CreateRouteFormPayload | Route)[] = useMemo(() => {
    return [
      ...routes,
      ...(value ?? []).filter((record: RecordRouteModel) => !isRouteId(record)),
    ] as (CreateRouteFormPayload | Route)[];
  }, [value, routes]);

  const routesOptions = useMemo(
    () =>
      existingRoutes.sort(nameComparator).map((record: CreateRouteFormPayload | Route) => ({
        label: (record as Route)?.id
          ? record.name
          : intl.formatMessage(messages.newRouteName, { name: record.name }),
        value: mapRouteRecordToValue((record as Route)?.id ?? record),
      })),
    [intl, existingRoutes]
  );

  const handleChange = useCallback(
    (selectedValue: string[]) => {
      const routePayloadByKey = (
        (value ?? []).filter(
          (record: RecordRouteModel) => !isRouteId(record)
        ) as CreateRouteFormPayload[]
      ).reduce<Record<string, CreateRouteFormPayload>>(
        (result, record: CreateRouteFormPayload) => ({
          ...result,
          [mapRouteRecordToValue(record)]: record,
        }),
        {}
      );
      onChange?.(
        selectedValue.map((record) =>
          record.includes(NEW_ROUTE_PREFIX) ? routePayloadByKey[record] : parseInt(record, 10)
        )
      );
    },
    [value, onChange]
  );

  const handleCreateRoute = useCallback(
    (route: CreateRouteFormPayload) => {
      hideCreateRouteModal();
      notification.success({
        description: intl.formatMessage(messages.routeAddedDescription),
        message: intl.formatMessage(messages.routeAddedMessage),
      });
      onChange?.([...(value ?? []), route]);
    },
    [hideCreateRouteModal, intl, onChange, value]
  );

  const handleSearch = useDebouncedCallback(
    [
      (value: string) => {
        onSearch({ entityType, query: value });
      },
      500,
    ],
    [entityType, onSearch]
  );

  return (
    <div>
      <div className={styles.selector}>
        <SelectField<string[]>
          allowClear
          dropdownMatchSelectWidth={false}
          filterOption={defaultFilterOption}
          loading={loading}
          mode="multiple"
          onChange={handleChange}
          onSearch={handleSearch}
          options={routesOptions}
          placeholder={intl.formatMessage(messages.placeholder)}
          showArrow
          showSearch
          value={parsedValue}
        />
      </div>
      <ButtonLink className={styles.newRouteBtn} onClick={handleCreateRouteClick}>
        {intl.formatMessage(messages.newRouteButton)}
      </ButtonLink>
      {createRouteModalVisible && (
        <CreateRoutePureModal
          onCreate={handleCreateRoute}
          onHide={hideCreateRouteModal}
          routes={existingRoutes}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  isMmcGeocodeLimitReached: isGeocodeMmcLimitReached(state),
  isOrgGeocodeLimitReached: isGeocodeOrgLimitReached(state),
  loading: areRoutesLoading(state),
  routes: getAddRecordsAvailableRoutes(state),
});

const mapDispatchToProps = {
  onSearch: filterRoutes.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(Routes);
